/**
 * Shared per-SignalCategory icon + label maps.
 *
 * Single source for components/signals/SignalCard.tsx,
 * components/signals/SignalFilters.tsx, and
 * components/home/TodaysQueueCard.tsx (GitHub #555) — previously each of
 * the first two hand-maintained its own copy; a third consumer (Today's
 * Queue, needing the same icon/label to disambiguate opportunities /
 * silent_expansion / advocate_readiness / expansion actions) made a shared
 * module worth factoring out rather than adding a fourth copy.
 *
 * Both maps are `Record<SignalCategory, ...>` so adding/removing a category
 * in types/signals.ts forces every consumer to be updated — it can't go
 * stale the way a hand-maintained array/switch can.
 */

import React from 'react';
import {
  AlertTriangle,
  TrendingUp,
  TrendingDown,
  Clock,
  Sparkles,
  MessageSquare,
  Activity,
  BarChart3,
  ShieldAlert,
  UserCheck,
  DollarSign,
  ClipboardCheck,
  CalendarClock,
  CalendarX,
} from 'lucide-react';
import type { SignalCategory, SignalConnectorType, SignalPriority } from '@/types/signals';

/**
 * Shared SignalPriority label map (GitHub #260). SignalFilters.tsx keeps its
 * own priority-option colors (filter-chip styling differs from the badge
 * look), but the label text itself belongs here so SignalCard's priority
 * badge can't drift from the filter's wording.
 */
export const priorityLabels: Record<SignalPriority, string> = {
  urgent: 'Urgent',
  high: 'High',
  medium: 'Medium',
  low: 'Low',
};

export interface SignalIconVisual {
  icon: React.ReactNode;
  symbol: string; // fallback symbol
}

export const categoryIcons: Record<SignalCategory, SignalIconVisual> = {
  churn_risk: { icon: React.createElement(AlertTriangle, { className: 'h-3.5 w-3.5' }), symbol: '⚠' },
  escalation: { icon: React.createElement(ShieldAlert, { className: 'h-3.5 w-3.5' }), symbol: '⚠' },
  sentiment: { icon: React.createElement(MessageSquare, { className: 'h-3.5 w-3.5' }), symbol: '◎' },
  expansion: { icon: React.createElement(TrendingUp, { className: 'h-3.5 w-3.5' }), symbol: '↑' },
  renewal: { icon: React.createElement(Clock, { className: 'h-3.5 w-3.5' }), symbol: '◷' },
  value_realization: { icon: React.createElement(DollarSign, { className: 'h-3.5 w-3.5' }), symbol: '$' },
  discovery_proposal: { icon: React.createElement(ClipboardCheck, { className: 'h-3.5 w-3.5' }), symbol: '✦' },
  other: { icon: React.createElement(Activity, { className: 'h-3.5 w-3.5' }), symbol: '◈' },
  crm_field_changes: { icon: React.createElement(Activity, { className: 'h-3.5 w-3.5' }), symbol: '◈' },
  health_trajectory: { icon: React.createElement(TrendingDown, { className: 'h-3.5 w-3.5' }), symbol: '◎' },
  health_scores: { icon: React.createElement(Activity, { className: 'h-3.5 w-3.5' }), symbol: '◉' },
  contact_changes: { icon: React.createElement(UserCheck, { className: 'h-3.5 w-3.5' }), symbol: '☍' },
  opportunities: { icon: React.createElement(TrendingUp, { className: 'h-3.5 w-3.5' }), symbol: '↑' },
  engagement: { icon: React.createElement(TrendingDown, { className: 'h-3.5 w-3.5' }), symbol: '◎' },
  engagement_decline: { icon: React.createElement(TrendingDown, { className: 'h-3.5 w-3.5' }), symbol: '◎' },
  disengagement: { icon: React.createElement(TrendingDown, { className: 'h-3.5 w-3.5' }), symbol: '◎' },
  silent_expansion: { icon: React.createElement(TrendingUp, { className: 'h-3.5 w-3.5' }), symbol: '↑' },
  advocate_readiness: { icon: React.createElement(Sparkles, { className: 'h-3.5 w-3.5' }), symbol: '★' },
  technical_risk: { icon: React.createElement(ShieldAlert, { className: 'h-3.5 w-3.5' }), symbol: '⚠' },
  operational: { icon: React.createElement(Activity, { className: 'h-3.5 w-3.5' }), symbol: '◈' },
  tickets: { icon: React.createElement(MessageSquare, { className: 'h-3.5 w-3.5' }), symbol: '✉' },
  communication: { icon: React.createElement(MessageSquare, { className: 'h-3.5 w-3.5' }), symbol: '✉' },
  response_time_degradation: { icon: React.createElement(Clock, { className: 'h-3.5 w-3.5' }), symbol: '◷' },
  activities: { icon: React.createElement(Activity, { className: 'h-3.5 w-3.5' }), symbol: '◈' },
  usage: { icon: React.createElement(BarChart3, { className: 'h-3.5 w-3.5' }), symbol: '↑' },
  product_changes: { icon: React.createElement(BarChart3, { className: 'h-3.5 w-3.5' }), symbol: '↑' },
  discovery: { icon: React.createElement(ClipboardCheck, { className: 'h-3.5 w-3.5' }), symbol: '✦' },
  support_trends: { icon: React.createElement(MessageSquare, { className: 'h-3.5 w-3.5' }), symbol: '✉' },
  email_sentiment: { icon: React.createElement(MessageSquare, { className: 'h-3.5 w-3.5' }), symbol: '◎' },
  meeting_cadence_decay: { icon: React.createElement(CalendarClock, { className: 'h-3.5 w-3.5' }), symbol: '◷' },
  playbook_urgency_escalation: { icon: React.createElement(AlertTriangle, { className: 'h-3.5 w-3.5' }), symbol: '⚠' },
  playbook_deadline_missed: { icon: React.createElement(CalendarX, { className: 'h-3.5 w-3.5' }), symbol: '⚠' },
  action_overdue: { icon: React.createElement(CalendarX, { className: 'h-3.5 w-3.5' }), symbol: '⚠' },
  playbook_deadline_due_soon: { icon: React.createElement(Clock, { className: 'h-3.5 w-3.5' }), symbol: '◷' },
  action_due_soon: { icon: React.createElement(Clock, { className: 'h-3.5 w-3.5' }), symbol: '◷' },
  operational_risks: { icon: React.createElement(ShieldAlert, { className: 'h-3.5 w-3.5' }), symbol: '⚠' },
  seat_utilization_drop: { icon: React.createElement(TrendingDown, { className: 'h-3.5 w-3.5' }), symbol: '◎' },
  seat_utilization_high: { icon: React.createElement(TrendingUp, { className: 'h-3.5 w-3.5' }), symbol: '◎' },
  technical_risks: { icon: React.createElement(ShieldAlert, { className: 'h-3.5 w-3.5' }), symbol: '⚠' },
  blockers: { icon: React.createElement(AlertTriangle, { className: 'h-3.5 w-3.5' }), symbol: '⚠' },
  onboarding_risks: { icon: React.createElement(ClipboardCheck, { className: 'h-3.5 w-3.5' }), symbol: '✦' },
  competitive_threat: { icon: React.createElement(ShieldAlert, { className: 'h-3.5 w-3.5' }), symbol: '⚠' },
};

export const categoryLabels: Record<SignalCategory, string> = {
  churn_risk: 'Churn Risk',
  escalation: 'Escalation',
  sentiment: 'Sentiment',
  expansion: 'Expansion',
  renewal: 'Renewal',
  value_realization: 'Value Realization',
  discovery_proposal: 'Discovery Proposal',
  crm_field_changes: 'CRM Field Changes',
  health_trajectory: 'Health Trajectory',
  health_scores: 'Health Score',
  contact_changes: 'Contact Changes',
  opportunities: 'Opportunities',
  engagement: 'Engagement',
  engagement_decline: 'Engagement Decline',
  disengagement: 'Disengagement',
  silent_expansion: 'Silent Expansion',
  advocate_readiness: 'Advocate Readiness',
  technical_risk: 'Technical Risk',
  operational: 'Operational',
  tickets: 'Tickets',
  communication: 'Communication',
  response_time_degradation: 'Response Time Degradation',
  activities: 'Activities',
  usage: 'Usage',
  product_changes: 'Product Changes',
  discovery: 'Discovery',
  support_trends: 'Support Trends',
  email_sentiment: 'Email Sentiment',
  meeting_cadence_decay: 'Meeting Cadence',
  playbook_urgency_escalation: 'Playbook Urgency',
  playbook_deadline_missed: 'Playbook Deadline Missed',
  action_overdue: 'Task Overdue',
  playbook_deadline_due_soon: 'Playbook Due Tomorrow',
  action_due_soon: 'Task Due Tomorrow',
  operational_risks: 'Operational Risk',
  seat_utilization_drop: 'Seat Utilization Drop',
  seat_utilization_high: 'Seat Utilization High',
  technical_risks: 'Technical Risks',
  blockers: 'Blockers',
  onboarding_risks: 'Onboarding Risks',
  competitive_threat: 'Competitive Threat',
  other: 'Other',
};

/**
 * Groups the 41 raw SignalCategory values into 14 CS-facing buckets for the
 * Signal Feed's category filter dropdown (SignalFilters.tsx), which renders
 * them alphabetically by group, then alphabetically by label within each
 * group — a flat 41-item checkbox list was hard to scan. Record<SignalCategory,
 * string> so it can't go stale the way categoryLabels/categoryIcons already
 * guard against above.
 */
export const categoryGroups: Record<SignalCategory, string> = {
  churn_risk: 'Churn Risk',
  competitive_threat: 'Churn Risk',
  health_scores: 'Health',
  health_trajectory: 'Health',
  sentiment: 'Sentiment',
  email_sentiment: 'Sentiment',
  engagement: 'Engagement',
  engagement_decline: 'Engagement',
  disengagement: 'Engagement',
  communication: 'Engagement',
  response_time_degradation: 'Engagement',
  meeting_cadence_decay: 'Engagement',
  activities: 'Engagement',
  usage: 'Usage & Adoption',
  seat_utilization_drop: 'Usage & Adoption',
  seat_utilization_high: 'Usage & Adoption',
  value_realization: 'Value Realization',
  tickets: 'Support',
  support_trends: 'Support',
  escalation: 'Support',
  technical_risk: 'Delivery Risk',
  technical_risks: 'Delivery Risk',
  operational: 'Delivery Risk',
  operational_risks: 'Delivery Risk',
  blockers: 'Delivery Risk',
  onboarding_risks: 'Delivery Risk',
  renewal: 'Renewal',
  expansion: 'Growth',
  silent_expansion: 'Growth',
  opportunities: 'Growth',
  advocate_readiness: 'Growth',
  playbook_urgency_escalation: 'Playbooks & Tasks',
  playbook_deadline_missed: 'Playbooks & Tasks',
  playbook_deadline_due_soon: 'Playbooks & Tasks',
  action_overdue: 'Playbooks & Tasks',
  action_due_soon: 'Playbooks & Tasks',
  discovery: 'Discovery',
  discovery_proposal: 'Discovery',
  crm_field_changes: 'Account Changes',
  contact_changes: 'Account Changes',
  product_changes: 'Account Changes',
  other: 'Other',
};

/**
 * Shared per-SignalConnectorType label map (Aug 2026 signal-system audit).
 * SignalFilters.tsx's connectorOptions used to be a hand-maintained array
 * that drifted from the SignalConnectorType union (missing 'discovery',
 * 'internal', 'system', and every write_agent_signal-originated connector) —
 * this Record<SignalConnectorType, string> gets the same exhaustiveness
 * guarantee categoryLabels already has, so a future connector addition to
 * the union can't be forgotten here without a type error.
 */
export const connectorLabels: Record<SignalConnectorType, string> = {
  salesforce: 'Salesforce',
  zendesk: 'Zendesk',
  gmail: 'Gmail',
  gong: 'Gong',
  amplitude: 'Amplitude',
  hubspot: 'HubSpot',
  internal: 'Internal',
  system: 'System',
  discovery: 'Discovery',
  health_scores: 'Health Score',
  health_trajectory: 'Health Trajectory',
  cross_connector: 'Cross-Connector',
  portfolio_activities: 'Activity',
  knowledge_base: 'Product',
  crm_field_changes: 'CRM Field Changes',
  technical_risk: 'Technical Risk',
  google_calendar: 'Google Calendar',
  playbook: 'Playbook',
  churn_predictor: 'Churn Predictor',
  renewal_analyzer: 'Renewal Analyzer',
  kickoff_workspace: 'Kickoff Workspace',
  health_score: 'Health Score Risk',
  usage_decline: 'Usage Decline',
  engagement_gap: 'Engagement Gap',
  support_ticket: 'Support Ticket',
  commitment: 'Commitment',
  ticket_scan: 'Ticket Scan',
  gong_transcript: 'Gong Transcript',
  action_queue: 'Action Queue',
};

/**
 * Task-derived detector categories (Action Queue / playbook step deadlines).
 *
 * The backend still writes these (the assignee's notification keys off them)
 * but never returns them from the Signal Feed (GitHub #1054), so they must not
 * be offered as a filter or added to the feed from a live SSE event. Mirrors
 * `TASK_DERIVED_SIGNAL_CATEGORIES` in `daily_digest/urgent_signal_service.py`.
 * The types/labels above stay, so the SignalCategory union keeps matching the
 * backend taxonomy.
 */
export const TASK_DERIVED_SIGNAL_CATEGORIES: ReadonlySet<string> = new Set([
  'action_overdue',
  'action_due_soon',
  'playbook_deadline_missed',
  'playbook_deadline_due_soon',
  'playbook_urgency_escalation',
]);
